import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { Popup } from '@/components/Popup';
import { ScoreSpinner } from '@/components/ui';
import type { Match, Prediction, Tournament } from '@/types';
import styles from './styles.module.scss';

interface PredictionFormProps {
  match: Match;
  tournament?: Tournament;
  tournamentId?: number | string;
  prediction?: Prediction;
  hideOddsInput?: boolean;
  onClose: () => void;
  onSuccess: () => void;
  homeTeamLogo?: string | null;
  awayTeamLogo?: string | null;
  leagueName?: string;
}

const formatKickoff = (date: Date): string => {
  const datePart = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${datePart} · ${timePart}`;
};

const PredictionForm = ({
  match,
  tournament,
  tournamentId,
  prediction,
  hideOddsInput = false,
  onClose,
  onSuccess,
  homeTeamLogo,
  awayTeamLogo,
  leagueName,
}: PredictionFormProps) => {
  const { t } = useTranslation(['tournament', 'common']);
  const queryClient = useQueryClient();
  const [scoreTeam1, setScoreTeam1] = useState<number>(prediction?.predicted_score_team1 ?? 0);
  const [scoreTeam2, setScoreTeam2] = useState<number>(prediction?.predicted_score_team2 ?? 0);
  const [oddsScore, setOddsScore] = useState<string>(prediction?.odds_score ? String(prediction.odds_score) : '');

  const [autoOdds, setAutoOdds] = useState<number | null>(null);
  const [autoOddsBookmaker, setAutoOddsBookmaker] = useState<string | null>(null);
  const [autoOddsLoading, setAutoOddsLoading] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const m = match as any;
  const team1Name = m.team1 || (typeof match.homeTeam === 'string' ? match.homeTeam : match.homeTeam?.name) || m.team1_short || '';
  const team2Name = m.team2 || (typeof match.awayTeam === 'string' ? match.awayTeam : match.awayTeam?.name) || m.team2_short || '';
  const currentTournamentId = tournament?.id || tournamentId;

  const homeLogo = homeTeamLogo ?? m.team1_logo_url ?? null;
  const awayLogo = awayTeamLogo ?? m.team2_logo_url ?? null;

  const rawDate = m.match_date || m.scheduledAt || m.matchDate || '';
  const kickoffText = rawDate ? formatKickoff(new Date(rawDate)) : '';
  const leagueText = leagueName || match.tournament_name || '';

  const getOutcome = (s1: number, s2: number) => {
    if (s1 > s2) return 'team1_win';
    if (s1 < s2) return 'team2_win';
    return 'draw';
  };

  const outcome = getOutcome(scoreTeam1, scoreTeam2);

  const getOutcomeText = (o: string) => {
    switch (o) {
      case 'team1_win': return t('tournament:win', { team: team1Name });
      case 'draw': return t('tournament:draw');
      case 'team2_win': return t('tournament:win', { team: team2Name });
      default: return '';
    }
  };

  const fetchAutoOdds = useCallback(async () => {
    if (!hideOddsInput || !currentTournamentId) return;
    const scoreKey = `${scoreTeam1}:${scoreTeam2}`;
    setAutoOddsLoading(true);
    setAutoOdds(null);
    setAutoOddsBookmaker(null);
    try {
      const response = await apiClient.getMatchOdds(
        String(currentTournamentId),
        String(match.id),
        scoreKey,
      );
      const data = response.data?.data;
      if (data && data.odds) {
        setAutoOdds(parseFloat(data.odds));
        setAutoOddsBookmaker(data.bookmaker_name || null);
      }
    } catch {
      // No odds for this score
    } finally {
      setAutoOddsLoading(false);
    }
  }, [hideOddsInput, currentTournamentId, match.id, scoreTeam1, scoreTeam2]);

  useEffect(() => {
    fetchAutoOdds();
  }, [fetchAutoOdds]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const data: any = {
        match_id: match.id,
        tournament_id: currentTournamentId,
        predicted_score_team1: scoreTeam1,
        predicted_score_team2: scoreTeam2,
        predicted_outcome: outcome,
      };
      if (!hideOddsInput) {
        data.odds_score = parseFloat(oddsScore);
      }

      if (prediction && prediction.id) {
        await apiClient.updatePrediction(String(prediction.id), data);
      } else {
        await apiClient.createPrediction(data);
      }
      // Invalidate the tournament-matches query so the user's prediction
      // shows up immediately on the tournament's Matches tab (each
      // `Match.user_prediction` field is derived from this query). The
      // backend ETag makes the refetch cheap (304 once the server is
      // unchanged). Centralised here rather than in each caller's
      // `onSuccess` so every prediction write path stays consistent —
      // MatchDetailLayout, DetailsTab inline save, NextMatch widget, etc.
      // Pass only the queryKey prefix so it matches both string- and
      // number-typed tournamentId variants the consumers happen to use.
      if (currentTournamentId !== undefined && currentTournamentId !== null) {
        queryClient.invalidateQueries({
          predicate: query => {
            const key = query.queryKey;
            return (
              Array.isArray(key) &&
              key[0] === 'tournament-matches' &&
              String(key[1]) === String(currentTournamentId)
            );
          },
        });
      }
      onSuccess();
    } catch (err: any) {
      const serverError = err.response?.data?.error;
      const serverMessage = err.response?.data?.message || serverError || err.message;
      toast.error(serverMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = hideOddsInput || (oddsScore !== '' && Number(oddsScore) >= 1);

  return (
    <>
      {isSubmitting && createPortal(
        <div className="spinner-overlay"><div className="spinner" /></div>,
        document.body,
      )}
      <Popup open={true} onClose={onClose} hideCloseButton noPadding>
        {/* Header */}
        <div className={styles.header}>
          <h3 className={styles.title}>
            {prediction ? t('tournament:editPredictionTitle') : t('tournament:yourPredictionTitle')}
          </h3>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <i className="icon-xmark" />
          </button>
        </div>

        {/* Fixture meta */}
        {(leagueText || kickoffText) && (
          <div className={styles.fixtureMeta}>
            {leagueText && <span className={styles.leagueName}>{leagueText}</span>}
            {kickoffText && <span className={styles.kickoff}>{kickoffText}</span>}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className={styles.formBody}>
          {/* Teams */}
          <div className={styles.teamsRow}>
            <div className={styles.teamCol}>
              {homeLogo && <img src={homeLogo} alt={team1Name} className={styles.teamLogo} />}
              <span className={styles.teamName}>{team1Name}</span>
            </div>
            <span className={styles.vs}>vs</span>
            <div className={styles.teamCol}>
              {awayLogo && <img src={awayLogo} alt={team2Name} className={styles.teamLogo} />}
              <span className={styles.teamName}>{team2Name}</span>
            </div>
          </div>

          {/* Score spinners */}
          <div className={styles.scoreRow}>
            <ScoreSpinner value={scoreTeam1} onChange={setScoreTeam1} min={0} max={20} />
            <span className={styles.scoreSeparator}>:</span>
            <ScoreSpinner value={scoreTeam2} onChange={setScoreTeam2} min={0} max={20} />
          </div>

          {/* Outcome */}
          <div className={styles.outcomeRow}>
            {t('tournament:outcomeLabel', { outcome: getOutcomeText(outcome) })}
          </div>

          {/* Coefficient — manual input */}
          {!hideOddsInput && (
            <div className={styles.coefficientField}>
              <label className={styles.fieldLabel} htmlFor="prediction-coef">
                {t('tournament:scoreCoeffLabel')}
              </label>
              <input
                id="prediction-coef"
                type="text"
                inputMode="decimal"
                value={oddsScore}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                  setOddsScore(v);
                }}
                placeholder=""
                className={styles.coefInput}
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Coefficient — auto from API */}
          {hideOddsInput && (
            <div className={styles.coefficientField}>
              <label className={styles.fieldLabel}>
                {t('tournament:scoreCoeffLabel')}
              </label>
              {autoOddsLoading ? null : autoOdds ? (
                <div className={styles.autoOddsDisplay}>
                  <span className={styles.autoOddsValue}>{autoOdds.toFixed(2)}</span>
                  {autoOddsBookmaker && (
                    <span className={styles.autoOddsBookmaker}>{autoOddsBookmaker}</span>
                  )}
                </div>
              ) : (
                <div className={styles.autoOddsNotAvailable}>
                  {t('tournament:oddsNotFound', { score: `${scoreTeam1}:${scoreTeam2}` })}
                </div>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className={styles.buttonRow}>
            <button
              type="button"
              className={styles.btnOutline}
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t('common:cancel')}
            </button>
            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={isSubmitting || !canSubmit}
            >
              {prediction ? t('tournament:updatePrediction') : t('common:save')}
            </button>
          </div>
        </form>
      </Popup>
    </>
  );
};

export default PredictionForm;
